import React, { FC, useMemo } from "react";

import {
	Button,
	Grid,
	InfoMessage,
	PasswordField,
	SectionCard,
} from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import UserService from "@/services/UserService";

import { PasskeysCard } from "@/components/organisms/PasskeysCard/PasskeysCard";
import { SessionsCard } from "@/components/organisms/SessionsCard/SessionsCard";

import { passwordSchema } from "@/utils/UForms";

import { TExtendedApiError, TSymfonyValidationError, TUser } from "@/types";

import { promiseToast } from "@/modules/index";

type TPasswordFormValues = {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
};

const passwordInitialValues: TPasswordFormValues = {
	currentPassword: "",
	newPassword: "",
	confirmPassword: "",
};

/** The API's 422 `{ errors: { field: msg } }` (or `msg[]`) as Formik errors. */
const apiFieldErrors = (
	error: unknown
): Partial<Record<keyof TPasswordFormValues, string>> | undefined => {
	const data = (error as AxiosError<TExtendedApiError>)?.response?.data;
	const errors = (data as TSymfonyValidationError | undefined)?.errors;
	if (!errors || typeof errors !== "object") return undefined;
	return Object.fromEntries(
		Object.entries(errors).map(([field, message]) => [
			field,
			Array.isArray(message) ? message[0] : String(message),
		])
	);
};

/**
 * The "Sécurité" tab: how I sign in - the password (changed here, or none
 * for an account through Google / GitHub / passkeys) and the passkeys.
 */
export const SecurityTab: FC<{ me: TUser }> = ({ me }) => {
	const passwordValidationSchema = useMemo(
		() =>
			Yup.object().shape({
				currentPassword: Yup.string().required(
					t("forms.password.required")
				),
				newPassword: passwordSchema(),
				confirmPassword: Yup.string()
					.required(t("forms.password.required"))
					.oneOf([Yup.ref("newPassword")], t("forms.password.match")),
			}),
		[]
	);

	const handlePasswordSubmit = (
		values: TPasswordFormValues,
		{ resetForm, setErrors }: FormikHelpers<TPasswordFormValues>
	) => {
		const prom = UserService.updatePassword(values).then(() => {
			resetForm();
		});
		promiseToast(prom);
		// The toast shows the API's message; a 422 also lands on its field.
		return prom.catch((err: unknown) => {
			const fieldErrors = apiFieldErrors(err);
			if (fieldErrors) setErrors(fieldErrors);
		});
	};

	return (
		<Grid columns={2} minItemWidth="400px">
			<SectionCard title={t("account.password.title")}>
				{me.hasPassword === false ? (
					<InfoMessage color="info">
						{t("account.password.sso")}
					</InfoMessage>
				) : (
					<Formik
						initialValues={passwordInitialValues}
						validationSchema={passwordValidationSchema}
						onSubmit={handlePasswordSubmit}
					>
						{({
							values,
							errors,
							touched,
							isSubmitting,
							setFieldValue,
						}) => {
							const password = (
								name: keyof TPasswordFormValues,
								label: string,
								autoComplete: string
							) => (
								<PasswordField
									name={name}
									label={label}
									autoComplete={autoComplete}
									value={values[name]}
									onChange={(value) =>
										setFieldValue(name, value)
									}
									error={
										touched[name] ? errors[name] : undefined
									}
								/>
							);
							return (
								<Form className="flex max-w-md flex-col gap-4">
									{password(
										"currentPassword",
										t("forms.password.current"),
										"current-password"
									)}
									{password(
										"newPassword",
										t("forms.password.new"),
										"new-password"
									)}
									{password(
										"confirmPassword",
										t("forms.password.confirm"),
										"new-password"
									)}
									<Button
										type="submit"
										className="self-start"
										isLoading={isSubmitting}
									>
										{t("account.password.submit")}
									</Button>
								</Form>
							);
						}}
					</Formik>
				)}
			</SectionCard>
			<PasskeysCard hasPassword={me.hasPassword !== false} />
			<SessionsCard />
		</Grid>
	);
};
